import moment from "moment";
import { exportToExcelMultiSheet } from "utils/excelHelpers";
import { getFormattedDateTime } from "utils/pdfDateUtils";
import { generatePDFFromSections } from "utils/pdfSectionCapture";
import { PRESETS } from "./teamHistoryFilter";
import { getTeamMatchDisplay } from "./teamMatchDisplay";

const PRESET_FILE_LABELS = {
  [PRESETS.LAST_3_SEASONS]: "Last_3_Seasons",
  [PRESETS.FULL_HISTORY]: "Full_History",
  [PRESETS.OLYMPIC_CYCLE]: "Olympic_Cycle",
};

const PRESET_DISPLAY_LABELS = {
  [PRESETS.LAST_3_SEASONS]: "Last 3 Seasons",
  [PRESETS.FULL_HISTORY]: "Full History",
  [PRESETS.OLYMPIC_CYCLE]: "Olympic Cycle",
};

function swapScore(score) {
  if (!score) return score;
  const parts = score.split("-");
  if (parts.length !== 2) return score;
  return `${parts[1]}-${parts[0]}`;
}

function formatExportValue(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  return value;
}

function getTeamPerspectiveScores(row) {
  const { isTargetTeamA } = getTeamMatchDisplay(row);
  const resultText = isTargetTeamA
    ? row.matchResultText
    : swapScore(row.matchResultText);

  if (!resultText || resultText === "TBD") {
    return { teamScore: "N/A", opponentScore: "N/A", winLose: "N/A" };
  }

  const [teamScore, opponentScore] = resultText.split("-").map((s) => s.trim());
  const teamSets = parseInt(teamScore, 10);
  const oppSets = parseInt(opponentScore, 10);

  let winLose = "N/A";
  const wOrL = isTargetTeamA ? row.teamA_w_or_l : row.teamB_w_or_l;
  if (wOrL === "W") {
    winLose = "Win";
  } else if (wOrL === "L") {
    winLose = "Loss";
  } else if (!Number.isNaN(teamSets) && !Number.isNaN(oppSets)) {
    winLose = teamSets > oppSets ? "Win" : "Loss";
  }

  return {
    teamScore: teamScore || "N/A",
    opponentScore: opponentScore || "N/A",
    winLose,
  };
}

export function getFilterDisplayLabel(activePreset, historyRange, selectedDate) {
  if (PRESET_DISPLAY_LABELS[activePreset]) {
    return PRESET_DISPLAY_LABELS[activePreset];
  }

  const end = moment(historyRange?.endDate || selectedDate).format("DD MMM YYYY");
  if (historyRange?.startYear != null && historyRange?.endYear != null) {
    return `${historyRange.startYear} – ${end}`;
  }

  return end;
}

export function getFilterFileLabel(activePreset, historyRange, selectedDate) {
  if (PRESET_FILE_LABELS[activePreset]) {
    return PRESET_FILE_LABELS[activePreset];
  }

  const end = moment(historyRange?.endDate || selectedDate).format("YYYY-MM-DD");
  const start =
    historyRange?.startYear != null
      ? `${historyRange.startYear}-01-01`
      : end;

  return `${start}_to_${end}`;
}

export function sanitizeFileNamePart(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function buildTeamHistoryFileName(teamName, gender, filterLabel, extension) {
  const parts = [
    "Team_History",
    sanitizeFileNamePart(teamName),
    gender === "Men" ? "Men" : "Women",
    sanitizeFileNamePart(filterLabel),
  ].filter(Boolean);

  return `${parts.join("_")}.${extension}`;
}

export function buildTeamHistoryExportRows(matches, exportedTeamName) {
  return (matches || []).map((row) => {
    const { isTargetTeamA, teamA, teamB } = getTeamMatchDisplay(row);
    const opponentName = isTargetTeamA ? teamB.name : teamA.name;
    const { teamScore, opponentScore, winLose } = getTeamPerspectiveScores(row);

    return {
      Date: moment(row.dateTimeUtc).isValid()
        ? moment(row.dateTimeUtc).format("DD MMM YYYY")
        : "N/A",
      Team: exportedTeamName || row.team_CalculatedName_target || "N/A",
      "Opposite team": opponentName || "N/A",
      "Exported team score": teamScore,
      "Opposite team score": opponentScore,
      "Win/Lose": winLose,
      "Event Name": formatExportValue(row.Event),
      City: formatExportValue(row.city),
      Country: formatExportValue(row.countryName),
      Hall: formatExportValue(row.hall),
      Spectators: formatExportValue(row.nbSpectators),
    };
  });
}

export function buildRankingPointsExportRows(
  chartData,
  teamName,
  gender,
  filterLabel
) {
  return (chartData || []).map((point) => ({
    Date: point.date || "N/A",
    "World Ranking Points": point.value ?? "N/A",
    Team: teamName || "N/A",
    Gender: gender || "N/A",
    "Date Range / Filter Label": filterLabel || "N/A",
  }));
}

export function buildRankingPositionExportRows(
  chartData,
  teamName,
  gender,
  filterLabel
) {
  return (chartData || []).map((point) => ({
    Date: point.date || "N/A",
    "World Ranking Position": point.value ?? "N/A",
    Team: teamName || "N/A",
    Gender: gender || "N/A",
    "Date Range / Filter Label": filterLabel || "N/A",
  }));
}

export function buildExportSummaryRows({
  teamName,
  gender,
  filterLabel,
  generatedAt,
  matchCount,
  rankingPointsCount,
  rankingPositionCount,
}) {
  return [
    { Field: "Team name", Value: teamName || "N/A" },
    { Field: "Gender", Value: gender || "N/A" },
    { Field: "Selected filter/preset", Value: filterLabel || "N/A" },
    { Field: "Export generated date/time", Value: generatedAt || "N/A" },
    { Field: "Number of matches exported", Value: matchCount ?? 0 },
    {
      Field: "Ranking points data points",
      Value: rankingPointsCount ?? 0,
    },
    {
      Field: "Ranking position data points",
      Value: rankingPositionCount ?? 0,
    },
  ];
}

export function exportTeamHistoryToXlsx(exportData) {
  const {
    teamName,
    gender,
    filterLabel,
    fileFilterLabel,
    matches,
    rankingPointsData,
    rankingPositionData,
  } = exportData;

  const generatedAt = getFormattedDateTime();
  const matchRows = buildTeamHistoryExportRows(matches, teamName);
  const rankingPointsRows = buildRankingPointsExportRows(
    rankingPointsData,
    teamName,
    gender,
    filterLabel
  );
  const rankingPositionRows = buildRankingPositionExportRows(
    rankingPositionData,
    teamName,
    gender,
    filterLabel
  );
  const summaryRows = buildExportSummaryRows({
    teamName,
    gender,
    filterLabel,
    generatedAt,
    matchCount: matchRows.length,
    rankingPointsCount: rankingPointsRows.length,
    rankingPositionCount: rankingPositionRows.length,
  });

  const fileName = buildTeamHistoryFileName(
    teamName,
    gender,
    fileFilterLabel,
    "xlsx"
  );

  exportToExcelMultiSheet(
    [
      { name: "Match History", data: matchRows },
      { name: "Ranking Points Data", data: rankingPointsRows },
      { name: "Ranking Position Data", data: rankingPositionRows },
      { name: "Export Summary", data: summaryRows },
    ],
    fileName
  );
}

export function waitForNextPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

export function waitForImagesToSettle(container, timeoutMs = 15000) {
  if (!container) {
    return Promise.resolve({ failed: 0, total: 0 });
  }

  const images = Array.from(container.querySelectorAll("img"));
  if (images.length === 0) {
    return Promise.resolve({ failed: 0, total: 0 });
  }

  return new Promise((resolve) => {
    let pending = images.length;
    let failed = 0;

    const finish = () => {
      resolve({ failed, total: images.length });
    };

    const timer = setTimeout(finish, timeoutMs);

    const onSettled = (isError) => {
      if (isError) {
        failed += 1;
      }
      pending -= 1;
      if (pending <= 0) {
        clearTimeout(timer);
        finish();
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        onSettled(img.naturalWidth === 0);
        return;
      }

      const onLoad = () => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
        onSettled(false);
      };
      const onError = () => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onError);
        onSettled(true);
      };

      img.addEventListener("load", onLoad);
      img.addEventListener("error", onError);
    });
  });
}

export async function exportTeamHistoryToPdf({
  captureElement,
  teamName,
  gender,
  fileFilterLabel,
  svgLogoPath,
}) {
  if (!captureElement) {
    throw new Error("Team History export target not found");
  }

  const captureWidth =
    captureElement.scrollWidth || captureElement.clientWidth;
  const captureHeight =
    captureElement.scrollHeight || captureElement.clientHeight;

  const captureDimensions = {
    refExists: true,
    scrollWidth: captureElement.scrollWidth,
    scrollHeight: captureElement.scrollHeight,
    clientWidth: captureElement.clientWidth,
    clientHeight: captureElement.clientHeight,
    boundingRect: captureElement.getBoundingClientRect(),
  };

  if (!captureWidth || !captureHeight) {
    console.error(
      "Team History export target has zero dimensions:",
      captureDimensions
    );
    throw new Error("Team History export target has zero dimensions");
  }

  // Ensure web fonts are loaded before capture so text is measured/rendered
  // with the real fonts. Never let a slow/failed font block the export.
  if (document.fonts?.ready) {
    try {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch (error) {
      console.warn("Team history PDF export: font readiness wait failed", error);
    }
  }

  const dateTimeString = getFormattedDateTime();
  const fileName = buildTeamHistoryFileName(
    teamName,
    gender,
    fileFilterLabel,
    "pdf"
  );

  try {
    await generatePDFFromSections({
      sections: [captureElement],
      fileName,
      svgLogoPath,
      dateTimeString,
    });
  } catch (error) {
    console.error("Team history PDF export failed:", error, {
      message: error?.message,
      stack: error?.stack,
      fileName,
      captureDimensions,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    });
    throw error;
  }
}
